import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  InputNumber,
  Checkbox,
  Space,
  Table,
  Typography,
  message,
  Form,
  Divider,
  Alert,
} from 'antd';
import {
  ApartmentOutlined,
  ReloadOutlined,
  PlusOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../services/api/krybotService';
import type {
  KrybotOrchestrationGraph,
  KrybotSpawnChildRunPayload,
} from '../../types/krybot';

const { Text, Paragraph } = Typography;

/**
 * Minimal admin UI for run orchestration APIs (DAG / invokes / spawn child run).
 * Does not execute runs — surfaces graph and worktree catalog from the backend.
 */
export const OrchestrationPanel: React.FC = () => {
  const [orchId, setOrchId] = useState('');
  const [graph, setGraph] = useState<KrybotOrchestrationGraph | null>(null);
  const [loadingGraph, setLoadingGraph] = useState(false);

  const [worktreeEdges, setWorktreeEdges] = useState<Array<{ from: string; to: string }>>([]);
  const [loadingEdges, setLoadingEdges] = useState(false);

  const [invokesAgentId, setInvokesAgentId] = useState('');
  const [invokes, setInvokes] = useState<string[]>([]);
  const [loadingInvokes, setLoadingInvokes] = useState(false);

  const [createForm] = Form.useForm<{ thread_id?: string; user_id?: string }>();
  const [spawnForm] = Form.useForm<
    KrybotSpawnChildRunPayload & { orchestration_id: string }
  >();

  useEffect(() => {
    spawnForm.setFieldsValue({ orchestration_id: orchId || undefined });
  }, [orchId, spawnForm]);

  const loadGraph = async () => {
    const id = orchId.trim();
    if (!id) {
      message.warning('Enter an orchestration UUID');
      return;
    }
    try {
      setLoadingGraph(true);
      const g = await KrybotService.getOrchestrationGraph(id);
      setGraph(g);
      message.success('Loaded orchestration graph');
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message || 'Failed to load graph');
      setGraph(null);
    } finally {
      setLoadingGraph(false);
    }
  };

  const loadWorktreeEdges = async () => {
    try {
      setLoadingEdges(true);
      const edges = await KrybotService.listWorktreeEdges();
      setWorktreeEdges(edges);
      message.success(`Loaded ${edges.length} worktree edge(s)`);
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message || 'Failed to load worktree edges');
    } finally {
      setLoadingEdges(false);
    }
  };

  const loadInvokes = async () => {
    const aid = invokesAgentId.trim();
    if (!aid) {
      message.warning('Enter an agent UUID (krybot_agents.id)');
      return;
    }
    try {
      setLoadingInvokes(true);
      const list = await KrybotService.listWorktreeInvokes(aid);
      setInvokes(list);
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message || 'Failed to load invokes');
      setInvokes([]);
    } finally {
      setLoadingInvokes(false);
    }
  };

  const handleCreateOrchestration = async (values: {
    thread_id?: string;
    user_id?: string;
  }) => {
    try {
      const rec = await KrybotService.createOrchestration({
        thread_id: values.thread_id || undefined,
        user_id: values.user_id || undefined,
      });
      setOrchId(rec.id);
      message.success(`Created orchestration ${rec.id}`);
      createForm.resetFields();
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message || 'Create failed');
    }
  };

  const handleSpawn = async (
    values: KrybotSpawnChildRunPayload & { orchestration_id: string }
  ) => {
    const oid = values.orchestration_id?.trim();
    if (!oid) {
      message.warning('orchestration_id required');
      return;
    }
    try {
      const seq =
        values.sequence_order === undefined || values.sequence_order === null
          ? 0
          : Number(values.sequence_order);
      const res = await KrybotService.spawnOrchestrationChildRun(oid, {
        parent_run_id: values.parent_run_id,
        target_agent_id: values.target_agent_id,
        input_payload: values.input_payload,
        invoke_key: values.invoke_key,
        sequence_order: Number.isFinite(seq) ? seq : 0,
        inherit_artifact_scope: values.inherit_artifact_scope !== false,
      });
      message.success(`Child run ${res.child_run_id} queued`);
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message || 'Spawn failed');
    }
  };

  const edgeColumns = [
    { title: 'From run', dataIndex: 'from_run_id', key: 'from', ellipsis: true },
    { title: 'To run', dataIndex: 'to_run_id', key: 'to', ellipsis: true },
    { title: 'Target agent', dataIndex: 'target_agent_id', key: 'ta', ellipsis: true },
    { title: 'Invoke key', dataIndex: 'invoke_key', key: 'ik', ellipsis: true },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Orchestration tools"
        description="Create and inspect DAG metadata and queued child runs. Run execution stays on the backend worker / process_run — this UI does not invoke the LLM."
      />

      <Card
        title={
          <span>
            <NodeIndexOutlined /> Worktree catalog (static)
          </span>
        }
        style={{ marginBottom: 16 }}
        size="small"
      >
        <Paragraph type="secondary">
          Declared invoke edges from <Text code>agent_worktree_schema.json</Text> (read-only).
        </Paragraph>
        <Space wrap style={{ marginBottom: 12 }}>
          <Button
            icon={<ReloadOutlined />}
            loading={loadingEdges}
            onClick={loadWorktreeEdges}
          >
            Load all edges
          </Button>
        </Space>
        <Table
          size="small"
          rowKey={(r) => `${r.from}-${r.to}`}
          dataSource={worktreeEdges}
          pagination={{ pageSize: 8 }}
          columns={[
            { title: 'From agent id', dataIndex: 'from', ellipsis: true },
            { title: 'To agent id', dataIndex: 'to', ellipsis: true },
          ]}
        />
        <Divider plain>Invokes for one agent</Divider>
        <Space wrap>
          <Input
            placeholder="Agent UUID (krybot_agents.id)"
            value={invokesAgentId}
            onChange={(e) => setInvokesAgentId(e.target.value)}
            style={{ width: 360 }}
          />
          <Button loading={loadingInvokes} onClick={loadInvokes}>
            Load invokes
          </Button>
        </Space>
        {invokes.length > 0 && (
          <ul style={{ marginTop: 12 }}>
            {invokes.map((id) => (
              <li key={id}>
                <Text code>{id}</Text>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title={
          <span>
            <ApartmentOutlined /> Orchestration session
          </span>
        }
        style={{ marginBottom: 16 }}
        size="small"
      >
        <Form layout="inline" onFinish={handleCreateOrchestration} form={createForm}>
          <Form.Item name="thread_id" label="Thread id">
            <Input placeholder="optional" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="user_id" label="User id">
            <Input placeholder="optional" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
              Create orchestration
            </Button>
          </Form.Item>
        </Form>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          After create, the new id is filled below for graph load and spawn.
        </Paragraph>

        <Divider plain>Load graph by id</Divider>
        <Space wrap>
          <Input
            placeholder="Orchestration UUID"
            value={orchId}
            onChange={(e) => setOrchId(e.target.value)}
            style={{ width: 360 }}
          />
          <Button type="primary" loading={loadingGraph} onClick={loadGraph}>
            Load graph
          </Button>
        </Space>

        {graph && (
          <div style={{ marginTop: 16 }}>
            <DescriptionsMini orch={graph} />
            <Table
              style={{ marginTop: 12 }}
              size="small"
              rowKey="id"
              dataSource={graph.edges}
              columns={edgeColumns}
              pagination={false}
            />
          </div>
        )}
      </Card>

      <Card title="Spawn child run (queued)" size="small">
        <Form layout="vertical" form={spawnForm} onFinish={handleSpawn}>
          <Form.Item name="orchestration_id" label="Orchestration id" rules={[{ required: true }]}>
            <Input placeholder="UUID" />
          </Form.Item>
          <Form.Item name="parent_run_id" label="Parent run id" rules={[{ required: true }]}>
            <Input placeholder="UUID" />
          </Form.Item>
          <Form.Item name="target_agent_id" label="Target agent id" rules={[{ required: true }]}>
            <Input placeholder="krybot_agents.id" />
          </Form.Item>
          <Form.Item name="invoke_key" label="Invoke key (optional)">
            <Input />
          </Form.Item>
          <Form.Item name="sequence_order" label="Sequence order" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="inherit_artifact_scope"
            valuePropName="checked"
            initialValue={true}
            label="Inherit artifact scope"
          >
            <Checkbox>Merge parent artifact scope onto child run</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit">
            Spawn child run
          </Button>
        </Form>
      </Card>
    </div>
  );
};

function DescriptionsMini({ orch }: { orch: KrybotOrchestrationGraph }) {
  const o = orch.orchestration;
  return (
    <Space direction="vertical" size="small">
      <Text>
        <strong>Status:</strong> {o.status}
      </Text>
      <Text>
        <strong>Thread:</strong> {o.thread_id || '—'}
      </Text>
      <Text>
        <strong>Root run:</strong> {o.root_run_id || '—'}
      </Text>
      <Text copyable>
        <strong>Id:</strong> {o.id}
      </Text>
    </Space>
  );
}
